import React, { ChangeEvent, useState } from 'react';
import { ErrorSummary, Label } from '@atoms/Label/Label';
import { RadioButton } from '@atoms/RadioButton/RadioButton';
import { InlineErrorMessage } from '@atoms/Errors/InlineErrorMessage';
import { ErrorMessages } from '@types';

export type SingleRadioOptionWithImageProps = {
  id?: string;
  name?: string;
  isDefault?: boolean;
  labelText: string;
  description: string;
  buttonRef?:
    | React.RefObject<HTMLInputElement>
    | React.MutableRefObject<HTMLInputElement>
    | null;
  radio: string;
  imageSrc: string;
  setRadio: (value: string) => void;
  onChange: (result: string) => void;
};

export interface RadioOptionsWithImagesProps {
  title?: string;
  description?: string;
  className?: string;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  onChange: (result: string) => void;
  isLabelTextTitle?: boolean;
  options: Omit<SingleRadioOptionWithImageProps, 'setRadio' | 'onChange'>[];
  isShowErrorSummary?: string;
}

const SingleRadioOptionWithImage: React.FC<SingleRadioOptionWithImageProps> = ({
  id,
  name,
  labelText,
  description,
  buttonRef,
  radio,
  setRadio,
  onChange,
  imageSrc,
}) => {
  const handleChange = (e: ChangeEvent) => {
    setRadio(labelText);
    onChange(labelText);
  };

  return (
    <div className="bg-background-paper relative flex w-full w-full flex-col items-center self-start overflow-hidden rounded shadow">
      <img
        src={imageSrc}
        alt={labelText}
        className="aspect-[3/2] h-full w-full object-cover"
      />
      <div className="flex w-full items-start p-4">
        <RadioButton
          id={id}
          ref={buttonRef}
          name={name}
          value={labelText}
          checked={radio === labelText}
          onChange={handleChange}
        />
        <div className="text-text-body mt-1.5 pl-3">
          <label
            className="text-bodyLg mb-1 inline-block cursor-pointer select-none font-bold after:absolute after:left-0 after:right-0 after:top-0 after:pb-[66.66%] after:content-['']"
            htmlFor={id}
          >
            {labelText}
          </label>
          {description ? (
            <p className="text-body whitespace-pre-wrap">{description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export function RadioOptionsWithImages(props: RadioOptionsWithImagesProps) {
  const defaultText = props.options.find(
    (option) => option.isDefault,
  )?.labelText;
  const [radioText, setRadioText] = useState(defaultText || '');
  const isShowErrorSummary = !props?.isShowErrorSummary;

  return (
    <form className="mb-1 mt-4">
      <fieldset>
        {isShowErrorSummary ? (
          <ErrorSummary messages={props.errorMessages} />
        ) : null}
        <Label
          isLegend
          className={`${props.className} !text-brand-primary body-2 text-h2`}
          htmlFor={props.title}
          text={props.title}
          isTitle={props.isLabelTextTitle}
          id={props.title}
        />
        {props.description ? (
          <p
            data-testid="description"
            className="text-text-body text-bodyLg my-[26px] font-normal leading-[28.5px]"
          >
            {props.description}
          </p>
        ) : null}

        <InlineErrorMessage
          path={props.errorPath}
          messages={props.errorMessages}
        />
        <div className="mt-[40px] grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {props.options.map((option, i) => (
            <SingleRadioOptionWithImage
              key={option.labelText}
              id={option.id || option.labelText + i}
              name={props.title}
              labelText={option.labelText}
              description={option.description}
              buttonRef={option.buttonRef}
              radio={radioText}
              setRadio={setRadioText}
              onChange={props.onChange}
              imageSrc={option.imageSrc}
            />
          ))}
        </div>
      </fieldset>
    </form>
  );
}

export default RadioOptionsWithImages;
